import Link from 'next/link';
import { useRouter } from 'next/router';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { useEffect, useState } from 'react';

import Button from 'components/app-components/ui/button';
import MaintenanceIcon from 'icons/MaintenanceIcon';
import { AuthRoutes, CommonRoutes } from 'routes';
import { openIntercomMessenger } from 'utils/intercom';
import { isSuperAdminReady, isSupportAdminMode } from 'utils/user';

const MaintenanceMode = ({ children }) => {
  const router = useRouter();
  const flagEnabled = useFeatureFlagEnabled('maintenance-mode');
  const [showIntercom, setShowIntercom] = useState(false);
  // If flag is ON, all companies are in maintenance mode
  // Super admins and support admins can ALWAYS bypass
  const isMaintenanceMode = flagEnabled && !isSuperAdminReady() && !isSupportAdminMode;

  useEffect(() => {
    // Check if Intercom is already available
    if ((window as any)?.Intercom) {
      setShowIntercom(true);
      return;
    }

    // Poll for Intercom availability in case it loads asynchronously
    const checkIntercom = () => {
      if ((window as any)?.Intercom) {
        setShowIntercom(true);
        return true;
      }
      return false;
    };

    // Check immediately and then poll every 5 seconds for up to 30 seconds
    if (!checkIntercom()) {
      const pollInterval = setInterval(() => {
        if (checkIntercom()) {
          clearInterval(pollInterval);
        }
      }, 5000);

      // Cleanup after 30 seconds
      const timeout = setTimeout(() => {
        clearInterval(pollInterval);
      }, 30000);

      return () => {
        clearInterval(pollInterval);
        clearTimeout(timeout);
        setShowIntercom(false);
      };
    }

    return () => {
      setShowIntercom(false);
    };
  }, []);
  const isAccessingSuperAdminPage = router.pathname === AuthRoutes.SuperAdmin;

  // Allow bypassing the maintenance mode if accessing the super-admin page
  if (isAccessingSuperAdminPage) return children;
  if (!isMaintenanceMode) return children;

  return (
    <>
      <header className="fixed top-0 z-10 h-[calc(64px+var(--safe-top))] w-[100vw] border-b border-neutral-200 bg-neutral-800 px-6 py-3 pt-[calc(0.75rem+var(--safe-top))]">
        <div className="mx-auto flex max-w-screen-lg items-center justify-center py-2.5">
          <Link href={CommonRoutes.Dashboard} className="flex items-center justify-center">
            <img className="h-6 w-auto" src="/logo/thera-logo-white-new.svg" alt="Thera-logo" />
          </Link>
        </div>
      </header>
      <main className="h-screen px-6 pt-[64px]">
        <div className="mx-auto flex h-full w-full max-w-lg flex-col items-center justify-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-md border border-neutral-200 bg-neutral-50">
            <MaintenanceIcon />
          </div>
          <h1 className="mb-4 mt-6 text-center text-2xl font-medium text-neutral-800">Scheduled Maintenance</h1>
          <div className="mb-6 rounded-md border border-neutral-100 bg-neutral-50 p-4 text-center md:p-6">
            <p className="mb-1 font-medium text-neutral-800">Scheduled maintenance in progress</p>
            <p className="text-sm font-normal text-neutral-500">Check back in 30 minutes!</p>
          </div>
          <p className="text-center text-sm font-normal text-neutral-600">
            Have any questions? Reach out to
            <br />
            <a href="mailto:support@getthera.com" className="underline">
              support@getthera.com
            </a>{' '}
            {showIntercom && (
              <>
                or{' '}
                <Button variant="plain" onClick={() => openIntercomMessenger()}>
                  chat with us <span className="mx-1 underline"> here</span>
                </Button>
              </>
            )}
          </p>
        </div>
      </main>
    </>
  );
};

export default MaintenanceMode;
